import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardHeader, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/components/ui/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { getForumCategories, createForumPost } from '@/lib/forum';
import type { ForumCategory } from '@/lib/types/forum';
import { MessageSquare, ArrowLeft, Loader2 } from 'lucide-react';

export default function NewDiscussion() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { toast } = useToast();
  const [categories, setCategories] = useState<ForumCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || '');

  useEffect(() => {
    if (!currentUser) {
      navigate('/login');
      return;
    }

    const loadCategories = async () => {
      try {
        const data = await getForumCategories();
        setCategories(data);
        if (!selectedCategory && data.length > 0) {
          setSelectedCategory(data[0].slug);
        }
      } catch (error) {
        console.error('Error loading categories:', error);
        toast({
          variant: "destructive",
          title: "Erreur",
          description: "Impossible de charger les catégories.",
        });
      } finally {
        setLoading(false);
      }
    };

    loadCategories();
  }, [currentUser, navigate, toast, selectedCategory]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentUser) return;

    try {
      setSubmitting(true);

      // Trouver la catégorie sélectionnée
      const category = categories.find(c => c.slug === selectedCategory);
      if (!category) {
        throw new Error('Catégorie invalide');
      }

      // Créer la discussion
      await createForumPost({
        title,
        content,
        userId: currentUser.id,
        categoryId: category.id,
        isPrivate: false,
        isPinned: false,
      });

      toast({
        title: "Discussion créée",
        description: "Votre discussion a été publiée avec succès.",
      });

      // Rediriger vers la catégorie
      navigate(`/forum/category/${selectedCategory}`);
    } catch (error) {
      console.error('Error creating post:', error);
      toast({
        variant: "destructive",
        title: "Erreur",
        description: "Impossible de créer la discussion.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 px-4 bg-gradient-to-br from-purple-50 via-pink-50 to-purple-100">
      <div className="max-w-4xl mx-auto space-y-6">
        <Card className="backdrop-blur-sm bg-white/90">
          <CardHeader className="flex flex-col space-y-4">
            <div className="flex items-center justify-between">
              <button
                onClick={() => navigate(-1)}
                className="text-[#54075B] hover:underline flex items-center gap-2"
              >
                <ArrowLeft className="w-4 h-4" />
                Retour
              </button>
            </div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <MessageSquare className="w-6 h-6 text-[#54075B]" />
              Nouvelle discussion
            </h1>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Catégorie
                </label>
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  required
                  className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                >
                  {categories.map((category) => (
                    <option key={category.id} value={category.slug}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Titre
                </label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  required
                  minLength={3}
                  maxLength={200}
                  className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="Donnez un titre à votre discussion"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Contenu
                </label>
                <Textarea
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  required
                  minLength={10}
                  className="min-h-[200px]"
                  placeholder="Rédigez votre message..."
                />
              </div>

              <div className="flex justify-end gap-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate(-1)}
                >
                  Annuler
                </Button>
                <Button
                  type="submit"
                  disabled={submitting}
                  className="bg-[#54075B] hover:bg-[#54075B]/90"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      Publication...
                    </>
                  ) : (
                    'Publier la discussion'
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}